import { jsPDF } from "jspdf";
import "jspdf-autotable";

// Helper to load logo with fallback
const loadLogo = async () => {
    try {
        // Try to import the logo dynamically
        const logoModule = await import("../assets/logo.png");
        return logoModule.default;
    } catch (error) {
        console.warn("Logo not found, PDF will be generated without logo");
        return null;
    }
};

// Helper to convert image URL to base64 (for external images)
const toBase64 = async (url) => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error("Error converting image to base64:", error);
        return null;
    }
};

// Algemene functie voor PDF-generatie
export const generatePDF = async (data) => {
    const doc = new jsPDF();
    const primaryColor = "#7495b4";
    const textColor = "#333";
    const logoWidth = 50;
    const logoHeight = 20;

    // Load logo with error handling
    let cursorY = 10;
    try {
        const logo = await loadLogo();
        if (logo) {
            doc.addImage(logo, "PNG", 10, 10, logoWidth, logoHeight);
            cursorY = 35;
        } else {
            cursorY = 10;
        }
    } catch (error) {
        console.warn("Could not load logo for PDF:", error);
        cursorY = 10;
    }

    const { user, selectedProject, hours, meters, extraWork, materials, totalHours, totalCosts, weekRange, status, statusTranslated, extraWorkImages = [], materialReceiptImages = [], rateType } = data;

    const { startOfWeek, endOfWeek } = weekRange;
    const weekTitle = `Weekstaat: ${startOfWeek.toLocaleDateString("nl-NL")} - ${endOfWeek.toLocaleDateString("nl-NL")}`;

    doc.setFontSize(18);
    doc.setTextColor(primaryColor);
    doc.text(weekTitle, 10, cursorY + 5);
    cursorY += 15;

    doc.setFontSize(12);
    doc.setTextColor(textColor);
    doc.text(`Ingediend door: ${user.name}`, 10, cursorY);
    cursorY += 10;

    // Add timesheet status if available
    if (status || statusTranslated) {
        const statusText = statusTranslated || status;
        doc.setFontSize(12);
        doc.setTextColor(primaryColor);
        doc.text(`Status: ${statusText}`, 10, cursorY);
        doc.setTextColor(textColor);
        cursorY += 10;
    }

    if (selectedProject) {
        doc.text(`Projectnaam: ${selectedProject.name}`, 10, cursorY);
        cursorY += 10;
        doc.text(`Projectlocatie: ${selectedProject.location}`, 10, cursorY);
        cursorY += 10;
        if (selectedProject.address) {
            doc.text(`Adres: ${selectedProject.address}`, 10, cursorY);
            cursorY += 10;
        }
    }

    cursorY += 5;
    doc.setFontSize(14);
    doc.setTextColor(primaryColor);
    doc.text(rateType === "meter" ? "Meters per Dag" : "Uren per Dag", 10, cursorY);
    cursorY += 7;

    const daysOfWeek = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
    const workData = rateType === "meter"
        ? daysOfWeek.map((day, index) => [day, `${meters?.[index] || 0} meter`])
        : daysOfWeek.map((day, index) => [day, `${hours?.[index] || 0} uur`]);

    // Add total row
    const totalLabel = rateType === "meter" ? "Totaal meters" : "Totaal uren";
    const totalValue = rateType === "meter" ? `${totalHours || 0} meter` : `${totalHours || 0} uur`;
    const totalRow = [
        { content: totalLabel, styles: { fontStyle: 'bold', textColor: primaryColor, fontSize: 13 } },
        { content: totalValue, styles: { fontStyle: 'bold', textColor: primaryColor, fontSize: 13 } }
    ];
    const workTableBody = [...workData, totalRow];

    doc.autoTable({
        startY: cursorY,
        head: [["Dag", rateType === "meter" ? "Meters" : "Uren"]],
        body: workTableBody,
        theme: "grid",
        styles: {
            fontSize: 11,
            textColor,
            cellPadding: 4,
            minCellHeight: 8,
            valign: 'middle',
            halign: 'left'
        },
        headStyles: {
            fillColor: primaryColor,
            textColor: "#fff",
            fontSize: 12,
            fontStyle: 'bold',
            cellPadding: 4,
            minCellHeight: 10,
            valign: 'middle'
        },
        margin: { left: 10, right: 10 }
    });
    cursorY = doc.lastAutoTable.finalY + 10;

    // Check if we need a new page
    if (cursorY > 250) {
        doc.addPage();
        cursorY = 20;
    }

    doc.setFontSize(14);
    doc.setTextColor(primaryColor);
    doc.text("Meerwerk", 10, cursorY);
    cursorY += 7;

    if (!extraWork || extraWork.length === 0) {
        doc.setFontSize(12);
        doc.setTextColor(textColor);
        doc.text("Geen meerwerk toegevoegd.", 10, cursorY);
        cursorY += 10;
    } else {
        const extraWorkData = extraWork.map((work, index) => [
            index + 1,
            work.omschrijving || "-",
            work.opmerkingen || "-",
        ]);
        doc.autoTable({
            startY: cursorY,
            head: [["#", "Omschrijving", "Opmerkingen"]],
            body: extraWorkData,
            theme: "grid",
            styles: {
                fontSize: 11,
                textColor,
                cellPadding: 4,
                minCellHeight: 8,
                valign: 'middle',
                halign: 'left'
            },
            headStyles: {
                fillColor: primaryColor,
                textColor: "#fff",
                fontSize: 12,
                fontStyle: 'bold',
                cellPadding: 4,
                minCellHeight: 10,
                valign: 'middle'
            },
            columnStyles: {
                0: { cellWidth: 15, halign: 'center' },
                1: { cellWidth: 80 },
                2: { cellWidth: 85 }
            },
            margin: { left: 10, right: 10 }
        });
        cursorY = doc.lastAutoTable.finalY + 10;

        // Render extraWorkImages after extra work table
        if (extraWorkImages && extraWorkImages.length > 0) {
            for (const item of extraWorkImages) {
                if (item.images && item.images.length > 0) {
                    for (const imgSrc of item.images) {
                        if (cursorY > 230) {
                            doc.addPage();
                            cursorY = 20;
                        }
                        try {
                            // Convert to base64 if needed
                            const imgData = imgSrc.startsWith('http') ? await toBase64(imgSrc) : imgSrc;
                            if (imgData) {
                                doc.addImage(imgData, "JPEG", 10, cursorY, 60, 60);
                                cursorY += 65;
                            }
                        } catch (error) {
                            console.error("Error adding image to PDF:", error);
                        }
                    }
                }
            }
        }
    }

    // Check if we need a new page
    if (cursorY > 250) {
        doc.addPage();
        cursorY = 20;
    }

    doc.setFontSize(14);
    doc.setTextColor(primaryColor);
    doc.text("Materialen", 10, cursorY);
    cursorY += 7;

    if (!materials || materials.length === 0) {
        doc.setFontSize(12);
        doc.setTextColor(textColor);
        doc.text("Geen materialen toegevoegd.", 10, cursorY);
        cursorY += 10;
    } else {
        const materialsData = materials.map((material, index) => [
            index + 1,
            material.name || "-",
            material.description || "-",
            material.quantity || "0",
            material.unit || "-",
            `€${(material.cost || 0).toFixed(2)}`,
        ]);
        // Add total row to materials table (bold)
        const totalMaterialsRow = [
            { content: "Totaal kosten materialen", colSpan: 5, styles: { fontStyle: 'bold', textColor: primaryColor, fontSize: 13, halign: 'right' } },
            { content: `€${(totalCosts || 0).toFixed(2)}`, styles: { fontStyle: 'bold', textColor: primaryColor, fontSize: 13 } }
        ];
        const materialsTableBody = [...materialsData, totalMaterialsRow];
        doc.autoTable({
            startY: cursorY,
            head: [["#", "Naam", "Beschrijving", "Hoeveelheid", "Eenheid", "Kosten"]],
            body: materialsTableBody,
            theme: "grid",
            styles: {
                fontSize: 11,
                textColor,
                cellPadding: 4,
                minCellHeight: 8,
                valign: 'middle',
                halign: 'left'
            },
            headStyles: {
                fillColor: primaryColor,
                textColor: "#fff",
                fontSize: 12,
                fontStyle: 'bold',
                cellPadding: 4,
                minCellHeight: 10,
                valign: 'middle'
            },
            columnStyles: {
                0: { cellWidth: 12, halign: 'center' },
                3: { cellWidth: 28, halign: 'center' },
                4: { cellWidth: 22, halign: 'center' },
                5: { cellWidth: 28, halign: 'right' }
            },
            margin: { left: 10, right: 10 }
        });
        cursorY = doc.lastAutoTable.finalY + 10;

        // Render material receipt images
        if (materialReceiptImages && materialReceiptImages.length > 0) {
            for (const item of materialReceiptImages) {
                if (item.images && item.images.length > 0) {
                    for (const imgSrc of item.images) {
                        if (cursorY > 230) {
                            doc.addPage();
                            cursorY = 20;
                        }
                        try {
                            const imgData = imgSrc.startsWith('http') ? await toBase64(imgSrc) : imgSrc;
                            if (imgData) {
                                doc.addImage(imgData, "JPEG", 10, cursorY, 60, 60);
                                cursorY += 65;
                            }
                        } catch (error) {
                            console.error("Error adding receipt image to PDF:", error);
                        }
                    }
                }
            }
        }
    }

    const fileName = `urenstaat-${startOfWeek.toLocaleDateString("nl-NL").replace(/\//g, '-')}.pdf`;
    doc.save(fileName);
    return doc;
};

// Nieuwe functie voor het genereren van project-PDF's
export const generateProjectPDF = async (project, autoSave = true) => {
    const doc = new jsPDF();
    const primaryColor = "#7495b4";
    const textColor = "#333";
    const logoWidth = 50;
    const logoHeight = 20;

    let cursorY = 10;

    // Load logo with error handling
    try {
        const logo = await loadLogo();
        if (logo) {
            doc.addImage(logo, "PNG", 10, 10, logoWidth, logoHeight);
            cursorY = 35;
        }
    } catch (error) {
        console.warn("Could not load logo for PDF:", error);
    }

    doc.setFontSize(18);
    doc.setTextColor(primaryColor);
    doc.text(`Projectinformatie: ${project.name || ''}`, 10, cursorY + 5);
    cursorY += 15;

    doc.setFontSize(12);
    doc.setTextColor(textColor);

    const fields = [
        ["Projectnaam", project.name || "-"],
        ["Locatie", project.location || "-"],
        ["Adres", project.address || "-"],
        ["Opdrachtgever", project.client || "-"],
        ["Projectprijs", project.projectprice ? `€${project.projectprice.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}` : "-"],
        ["Projecttarief", project.projectrate ? `€${project.projectrate.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}` : "-"],
        ["Materiaalkosten", project.materialcosts ? `€${project.materialcosts.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}` : "-"],
        ["Winst", project.profit ? `€${project.profit.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}` : "-"],
        ["Startdatum", project.start_date ? new Date(project.start_date).toLocaleDateString("nl-NL") : "-"],
        ["Einddatum", project.end_date ? new Date(project.end_date).toLocaleDateString("nl-NL") : "-"],
        ["WhatsApp", project.whatsapp || "-"],
        ["Aangemaakt op", project.created_at ? new Date(project.created_at).toLocaleDateString("nl-NL") : "-"],
        ["Bijgewerkt op", project.updated_at ? new Date(project.updated_at).toLocaleDateString("nl-NL") : "-"],
    ];

    doc.autoTable({
        startY: cursorY,
        head: [["Veld", "Waarde"]],
        body: fields,
        theme: "grid",
        styles: {
            fontSize: 11,
            textColor,
            cellPadding: 4,
            minCellHeight: 8,
            valign: 'middle',
            halign: 'left'
        },
        headStyles: {
            fillColor: primaryColor,
            textColor: "#fff",
            fontSize: 12,
            fontStyle: 'bold',
            cellPadding: 4,
            minCellHeight: 10,
            valign: 'middle'
        },
        columnStyles: {
            0: { cellWidth: 50, fontStyle: 'bold' },
            1: { cellWidth: 130 }
        },
        margin: { left: 10, right: 10 }
    });

    cursorY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setTextColor("#888");
    doc.text("Gegenereerd op: " + new Date().toLocaleString("nl-NL"), 10, cursorY);

    if (autoSave) {
        const fileName = `project-${(project.name || 'informatie').replace(/[^a-z0-9]/gi, '_')}.pdf`;
        doc.save(fileName);
    }

    return doc;
};
